// Профиль /tutors/<slug>: блок «О себе» и ряд кнопок соцсетей.
// Разбор текста — src/lib/partner-about.ts; здесь только React-узлы:
// строки, <strong>, <em>, <a>. Никакого dangerouslySetInnerHTML.
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from "react";

import { MAP_COPY } from "./map-copy";
import type { SiteLang } from "@/components/site/SiteChrome";
import {
  aboutIsLong,
  parseAbout,
  socialLabel,
  type AboutNode,
  type SocialLink,
  type SocialService,
} from "@/lib/partner-about";

export function renderAboutNodes(nodes: AboutNode[]): ReactNode[] {
  return nodes.map((n, i) => {
    if (n.t === "text") return <Fragment key={i}>{n.v}</Fragment>;
    if (n.t === "b") return <strong key={i}>{renderAboutNodes(n.c)}</strong>;
    if (n.t === "i") return <em key={i}>{renderAboutNodes(n.c)}</em>;
    return (
      <a key={i} href={n.href} target="_blank" rel="noopener nofollow ugc">
        {n.label}
      </a>
    );
  });
}

/** Текст описания: строка за строкой, пустая строка — отступ. */
export function AboutText({ lines }: { lines: AboutNode[][] }) {
  return (
    <>
      {lines.map((line, i) =>
        line.length ? (
          <p key={i}>{renderAboutNodes(line)}</p>
        ) : (
          <p key={i} className="tm-about__gap" aria-hidden="true" />
        ),
      )}
    </>
  );
}

/* Значки — свои простые глифы, без зависимостей. currentColor. */
const ICON: Record<SocialService, ReactNode> = {
  youtube: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="4" fill="currentColor" />
      <path d="M10 9v6l5-3z" fill="#fff" />
    </>
  ),
  instagram: (
    <>
      <rect
        x="3"
        y="3"
        width="18"
        height="18"
        rx="5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="17.3" cy="6.7" r="1.2" fill="currentColor" />
    </>
  ),
  whatsapp: (
    <>
      <path
        d="M12 3a9 9 0 0 0-7.8 13.5L3 21l4.6-1.2A9 9 0 1 0 12 3z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M9 8.2c.3-.4.8-.3 1 .1l.7 1.6c.1.3 0 .6-.2.8l-.5.5c.6 1.2 1.6 2.2 2.8 2.8l.5-.5c.2-.2.5-.3.8-.2l1.6.7c.4.2.5.7.1 1-.7.8-1.6 1.1-2.6.8-2.3-.8-4.2-2.7-5-5-.3-1 0-1.9.8-2.6z"
        fill="currentColor"
      />
    </>
  ),
  telegram: (
    <path
      d="M21 4 2.8 11.1c-.9.4-.9 1.6.1 1.9l4.6 1.4 1.8 5.4c.3.8 1.3 1 1.9.4l2.6-2.4 4.6 3.4c.7.5 1.6.1 1.8-.7L22.3 5.3c.2-.9-.6-1.6-1.3-1.3zM9.6 14.1l8-6.3-6.3 7.4-.3 3z"
      fill="currentColor"
    />
  ),
  facebook: (
    <path
      d="M13.5 21v-7.5h2.6l.4-3h-3V8.6c0-.9.3-1.5 1.5-1.5h1.6V4.4A21 21 0 0 0 14.3 4c-2.4 0-4 1.4-4 4.1v2.4H7.7v3h2.6V21z"
      fill="currentColor"
    />
  ),
};

export function SocialButtons({
  links,
  label,
  lang,
}: {
  links: SocialLink[];
  label: string;
  lang: SiteLang;
}) {
  if (!links.length) return null;
  return (
    <ul className="tm-social" aria-label={label}>
      {links.map((l) => (
        <li key={l.href}>
          <a
            className={`tm-social__a tm-social__a--${l.service}`}
            href={l.href}
            target="_blank"
            rel="noopener nofollow ugc"
            title={l.href}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
              {ICON[l.service]}
            </svg>
            <span>{socialLabel(l, lang)}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

/**
 * «О себе»: заголовок, текст, кнопки соцсетей. Длинный текст свёрнут до
 * 8 строк с кнопкой «Ещё». Длинный ли — решает текст (aboutIsLong), поэтому
 * кнопка есть уже в разметке сервера. Если на широком экране текст всё же
 * влез — кнопка убирается (она стоит ПОД текстом, текст не сдвигается).
 */
export function AboutBlock({ about, lang, labels }: { about: string; lang: SiteLang; labels?: { title: string; more: string; less: string } }) {
  const c = MAP_COPY[lang];
  // Профиль (28.10.2026): «Haqqımda» / «Davamı» — свои подписи; без них — как раньше.
  const l = labels ?? { title: c.aboutTitle, more: c.aboutMore, less: c.aboutLess };
  const parsed = parseAbout(about);
  const long = aboutIsLong(parsed);
  const [open, setOpen] = useState(false);
  const [fits, setFits] = useState(false);
  const body = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    const el = body.current;
    if (long && el && el.scrollHeight <= el.clientHeight + 2) setFits(true);
  }, [long]);
  const clamp = long && !fits;
  if (!parsed.lines.length && !parsed.socials.length) return null;
  return (
    <section className="tm-aboutbox" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} className="tm-aboutbox__h">
        {l.title}
      </h2>
      {parsed.lines.length > 0 && (
        <div
          ref={body}
          id={`${id}-t`}
          className={`tm-about${clamp && !open ? " tm-about--clamp" : ""}`}
        >
          <AboutText lines={parsed.lines} />
        </div>
      )}
      {clamp && (
        <button
          type="button"
          className="tm-about__more"
          aria-expanded={open}
          aria-controls={`${id}-t`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? l.less : l.more}
        </button>
      )}
      <SocialButtons links={parsed.socials} label={c.links} lang={lang} />
    </section>
  );
}
